import dayjs from 'dayjs';
import { UserIcon } from 'lucide-react';
import type { ReactElement } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

import { useChatApi, useChatBranding, useChatConfig } from '../ChatProvider';
import type { Message, PodcastMessage, TextMessage, UserAudioMessage } from '../state/types';
import { AnswerActions } from './AnswerActions';
import { AudioPlayer } from './AudioPlayer';
import { MarkdownMessage } from './MarkdownMessage';
import { TypingIndicator } from './TypingIndicator';

interface MessageBubbleProps {
  message: Message;
  isLastActionableBot?: boolean;
}

export function MessageBubble({
  message,
  isLastActionableBot = false,
}: MessageBubbleProps): ReactElement {
  if (message.kind === 'podcast') {
    return <PodcastBubble message={message} />;
  }
  if (message.kind === 'user-audio') {
    return <UserAudioBubble message={message} />;
  }
  return <TextBubble message={message} isLastActionableBot={isLastActionableBot} />;
}

/** Áudio que o próprio usuário gravou, com player para reouvir (US 13335). */
function UserAudioBubble({ message }: { message: UserAudioMessage }): ReactElement {
  const config = useChatConfig();

  return (
    <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-300 flex w-full justify-end">
      <div className="flex max-w-[85%] flex-col items-end gap-2">
        <div className="bg-primary/10 border-primary/20 flex w-full flex-col gap-2 rounded-3xl rounded-tr-md border px-4 py-3 shadow-sm">
          <p className="text-foreground text-sm leading-snug">{message.text}</p>
          <AudioPlayer src={message.audioUrl} durationHintSec={message.durationMs / 1000} />
        </div>

        {config.enableTimestamps && (
          <time
            dateTime={new Date(message.createdAt).toISOString()}
            className="text-muted-foreground/80 px-1 text-[10.5px] tabular-nums"
          >
            {dayjs(message.createdAt).format('HH:mm')}
          </time>
        )}
      </div>
    </div>
  );
}

function TextBubble({
  message,
  isLastActionableBot,
}: {
  message: TextMessage;
  isLastActionableBot: boolean;
}): ReactElement {
  const branding = useChatBranding();
  const config = useChatConfig();
  const isUser = message.sender === 'user';
  const showTyping = message.isTyping === true && message.text.length === 0;
  const showActions =
    !isUser &&
    message.hideActions !== true &&
    message.isStreaming !== true &&
    !showTyping &&
    message.text.length > 0;

  return (
    <div
      className={cn(
        'flex w-full gap-4 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-300',
        isUser ? 'flex-row-reverse' : 'flex-row',
      )}
    >
      {isUser ? (
        <UserAvatar src={branding.userAvatar} />
      ) : (
        <BotAvatar src={branding.logo} title={branding.title} />
      )}

      <div
        className={cn(
          'flex flex-col gap-2',
          isUser ? 'max-w-[85%] items-end' : 'min-w-0 flex-1 items-start',
        )}
      >
        <div
          className={cn(
            'text-sm',
            isUser
              ? 'bg-primary text-primary-foreground rounded-3xl rounded-tr-md px-4 py-3 shadow-sm'
              : 'text-foreground w-full',
          )}
          data-sender={message.sender}
        >
          {showTyping ? (
            <TypingIndicator />
          ) : isUser ? (
            <MarkdownMessage
              text={message.text}
              className="text-primary-foreground [&_a]:text-primary-foreground [&_a]:underline"
            />
          ) : (
            <MarkdownMessage text={message.text} />
          )}
        </div>

        <div
          className={cn('flex items-center gap-2 px-1', isUser ? 'flex-row-reverse' : 'flex-row')}
        >
          {config.enableTimestamps && (
            <time
              dateTime={new Date(message.createdAt).toISOString()}
              className="text-muted-foreground/80 text-[10.5px] tabular-nums"
            >
              {dayjs(message.createdAt).format('HH:mm')}
            </time>
          )}
          {showActions && (
            <AnswerActions
              message={message}
              showPodcastCta={isLastActionableBot}
              podcastEnabled={config.enablePodcast}
              copyEnabled={config.enableCopyButton}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PodcastBubble({ message }: { message: PodcastMessage }): ReactElement {
  const branding = useChatBranding();
  const config = useChatConfig();
  const api = useChatApi();

  return (
    <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-300 flex w-full gap-4">
      <BotAvatar src={branding.logo} title={branding.title} />

      <div className="flex max-w-[85%] flex-1 flex-col gap-2">
        <div className="from-primary/10 to-primary/5 border-primary/20 flex flex-col gap-3 rounded-3xl rounded-tl-md border bg-gradient-to-br px-4 py-3 shadow-sm">
          <p className="text-foreground text-sm leading-snug">{message.text}</p>
          <AudioPlayer
            src={message.audioUrl}
            durationHintSec={message.durationSec ?? undefined}
            showDownload
            onDownload={() => {
              void api.downloadPodcast(message.audioUrl);
            }}
          />
        </div>

        {config.enableTimestamps && (
          <time
            dateTime={new Date(message.createdAt).toISOString()}
            className="text-muted-foreground/80 px-1 text-[10.5px] tabular-nums"
          >
            {dayjs(message.createdAt).format('HH:mm')}
          </time>
        )}
      </div>
    </div>
  );
}

function BotAvatar({ src, title }: { src: string; title: string }): ReactElement {
  return (
    <Avatar className="ring-primary/15 mt-1 size-9 shrink-0 ring-2 ring-offset-2 ring-offset-background">
      <AvatarImage src={src} alt="" />
      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
        {initials(title)}
      </AvatarFallback>
    </Avatar>
  );
}

function UserAvatar({ src }: { src: string | undefined }): ReactElement {
  return (
    <Avatar
      aria-hidden
      className="ring-border mt-1 size-9 shrink-0 ring-2 ring-offset-2 ring-offset-background"
    >
      {src && <AvatarImage src={src} alt="" />}
      <AvatarFallback className="bg-muted text-muted-foreground">
        <UserIcon className="size-4" />
      </AvatarFallback>
    </Avatar>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
